import * as H from 'history';
import { History, LocationDescriptor, Path } from 'history';

const getBasePathname = () => {
  if (process.env.BASE_URL.startsWith('/')) {
    return process.env.BASE_URL.substr(1);
  }
  return process.env.BASE_URL;
};

const getLangFromPathname = (pathname: string, defaultLang: string = process.env.LANG_DEFAULT): string => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.indexOf(getBasePathname()) === 1) {
    return parts[0];
  }

  return defaultLang;
};

const setLangToPathname = (pathname: string | undefined, lang: string = process.env.LANG_DEFAULT): string => {
  if (!pathname) {
    return pathname;
  }

  const parts = pathname.split('/').filter(Boolean);
  const langFromPathname = getLangFromPathname(pathname, null);
  let pathnameWithoutLang = pathname;

  if (langFromPathname) {
    const partsWithoutLang = [...parts];
    partsWithoutLang.splice(0, 1);
    pathnameWithoutLang = `/${partsWithoutLang.join('/')}`;
  }

  if (lang === process.env.LANG_DEFAULT) {
    return pathnameWithoutLang;
  }

  return `/${lang}${pathnameWithoutLang}`;
};

const setBasenameToPathname = (pathname: string | undefined): string => {
  if (!pathname) {
    return pathname;
  }

  const langFromPathname = getLangFromPathname(pathname, null);
  const parts = pathname.split('/').filter(Boolean);

  if (
    (!langFromPathname && parts[0] === getBasePathname())
    || (langFromPathname && parts[1] === getBasePathname())
  ) {
    return pathname;
  }

  return process.env.BASE_URL + pathname;
};

const buildPathname = (pathname: string | undefined, lang: string) => {
  if (!pathname) {
    return pathname;
  }

  return setLangToPathname(setBasenameToPathname(pathname), lang);
};

const buildLinkTo = <T>(to: T | H.LocationDescriptor, lang: string): T | H.LocationDescriptor => {
  if (typeof to === 'string') {
    return buildPathname(to, lang);
  }

  if (typeof to === 'object' && 'pathname' in to) {
    return { ...to, pathname: buildPathname(to.pathname, lang) };
  }

  return to;
};

const setLangToLocationDescriptor = (
  locationDescriptor: LocationDescriptor<History.LocationState> | Path,
  lang: string,
): LocationDescriptor<History.LocationState> | Path => {
  let locationDescriptorNew: LocationDescriptor<History.LocationState>;
  if (typeof locationDescriptor === 'string') {
    locationDescriptorNew = buildPathname(locationDescriptor, lang);
  } else {
    locationDescriptorNew = { ...locationDescriptor };
    if ('pathname' in locationDescriptorNew) {
      locationDescriptorNew.pathname = buildPathname(locationDescriptor.pathname, lang);
    }
  }

  return locationDescriptorNew;
};

export {
  getLangFromPathname,
  setLangToPathname,
  setBasenameToPathname,
  buildPathname,
  buildLinkTo,
  setLangToLocationDescriptor,
};
